import { useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import type { MeshData } from '@shared/types';
import { supabase } from '@/lib/supabase';
import { putCachedMeshBlob } from '@/lib/meshBlobCache';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AuthContext, type BillingStatus } from './AuthContext';
import {
  claimDefaultWorkspace,
  isAccountClaimed,
  isDefaultUser,
} from '@/lib/defaultWorkspace';
import { CONVERSATIONS_QUERY_KEY } from '@/lib/conversationsQuery';
import { readAnonZeroOverride } from '@/lib/devAnonZero';
import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import {
  recordAccountFromSession,
  updateStoredProfile,
} from '@/lib/accountsStore';
import { AuthPopup } from '@/components/auth/AuthPopup';

// ── God mode (dev-only) ──────────────────────────────────────────────────────
// Local `npm run dev` only. Goal: dev behaves EXACTLY like prod — a REAL session
// (anonymous or real sign-in) with a REAL user id, so user-scoped DB queries,
// thumbnails, and profiles all work and match prod. The ONLY thing god mode fakes
// is BILLING (unlimited pro tokens) so dev isn't token-gated. We deliberately do
// NOT fabricate a user/session: the old `__god_mode__` placeholder id leaked into
// real queries (invalid-uuid errors, a false 404 on reopen, skewed thumbnails)
// and was the root cause of "local diverges from prod". import.meta.env.DEV is
// false in every built bundle (prod AND staging), so this block is tree-shaken
// from deployed builds. `VITE_E2E=true` (Playwright webServer) also forces real
// billing so the E2E gate exercises production behavior.
const GOD_MODE = import.meta.env.DEV && import.meta.env.VITE_E2E !== 'true';

const GOD_BILLING: BillingStatus = {
  user: { hasTrialed: true },
  subscription: { level: 'pro', status: 'active', currentPeriodEnd: null },
  tokens: { free: 99999, subscription: 0, purchased: 0, total: 99999 },
};
// ─────────────────────────────────────────────────────────────────────────────

const ensurePermission = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const perm = await Notification.requestPermission();
  return perm === 'granted';
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const storedSession = JSON.parse(
    localStorage.getItem('session') ?? 'null',
  ) as Session | null;
  const [session, setSession] = useState<Session | null>(storedSession);
  // Seed user from stored session so god mode never uses __god_mode__ ID
  // for DB operations when a real session already exists in localStorage.
  const [user, setUser] = useState<User | null>(storedSession?.user ?? null);
  // Warm-load fast path: when a still-valid cached session exists, start
  // un-gated so the shell renders on first paint (session/user are already
  // seeded from storage above) instead of showing a loader until
  // refreshSession() returns (~0.5–1.5s "token-refresh" on the critical path).
  // refreshSession still runs in the effect below to rotate the token; the
  // Supabase client keeps it fresh (autoRefreshToken), and the >60s validity
  // buffer means in-flight queries use a token that isn't about to expire.
  // (Page-load optimization — issue #58.)
  const [isLoading, setIsLoading] = useState(() => {
    // Same warm/cold logic for god mode and prod: skip the loader only when a
    // still-valid cached session is already seeded; otherwise wait for the real
    // session (refreshSession or signInAnonymously) to resolve before consumers
    // run user-scoped queries.
    return !(
      storedSession?.expires_at &&
      storedSession.expires_at > Math.floor(Date.now() / 1000) + 60
    );
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Multi-account switching (#297). `baseSignOut` is the plain supabase sign-out
  // the switcher falls back to when no other stored account is available.
  const baseSignOut = useRef(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }).current;
  const accounts = useAccountSwitcher(queryClient, baseSignOut);

  // Initialize auth state and set up session listener
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.refreshSession();
        if (session) {
          setSession(session);
          localStorage.setItem('session', JSON.stringify(session));
          setUser(session.user ?? null);
        } else {
          let deviceId = localStorage.getItem('device_id');
          if (!deviceId) {
            deviceId = crypto.randomUUID();
            localStorage.setItem('device_id', deviceId);
          }
          const { data: anonData } = await supabase.auth.signInAnonymously();
          if (anonData.session) {
            setSession(anonData.session);
            localStorage.setItem('session', JSON.stringify(anonData.session));
            setUser(anonData.session.user);
            // Fire-and-forget. This registers the anon session for abuse
            // tracking + token allocation, but nothing on the initial render
            // path needs it to have completed. Awaiting it here gated the whole
            // app-ready state (setIsLoading(false) in the finally) — and with it
            // the conversations/notifications/thumbnails query wave — on an
            // extra RPC roundtrip on the critical path. Let it resolve in the
            // background; the billing query picks up token balances on its own
            // cadence. (Page-load optimization — see issue #58.)
            // The PostgREST rpc builder is a thenable, not a real Promise, so
            // `.catch` on it throws "catch is not a function". Adopt it into a
            // real Promise first so the fire-and-forget rejection is handled.
            void Promise.resolve(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (supabase.rpc as any)('process_anonymous_session', {
                p_user_id: anonData.session.user.id,
                p_fingerprint: deviceId,
              }),
            ).catch((e: unknown) =>
              console.error('process_anonymous_session failed', e),
            );
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      localStorage.setItem('session', JSON.stringify(session));
      setUser(session?.user ?? null);
      // Multi-account store (#297): record/refresh the active account on any
      // real (non-anon) session — sign-in, token rotation, or an account switch
      // all flow through here, keeping stored tokens fresh. Anonymous sessions
      // are intentionally skipped inside recordAccountFromSession. Notify the
      // switcher in-tab (the `storage` event only fires for OTHER tabs).
      if (session) {
        recordAccountFromSession(session);
        window.dispatchEvent(new Event('tk-accounts-changed'));
      }
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/update-password');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Billing state + token balances from telekinesis-billing (source of truth).
  //
  // Event-driven via the mesh-updated realtime handler below, which invalidates
  // ['billing','status'] when a generation finishes (the only in-session path that
  // spends tokens). A foreground-only 60s fallback poll (see refetchInterval below)
  // covers billing changes with no mesh broadcast — Stripe subscription up/down-grade,
  // one-time token-pack purchases, trial/period rollover — without wasting polls on
  // backgrounded or anonymous tabs (issue #167). `refetchOnWindowFocus` (RQ default)
  // additionally catches the common return-from-Stripe-checkout case.
  const { data: billing, isLoading: isBillingLoading } = useQuery({
    queryKey: ['billing', 'status'],
    // God mode fully fakes billing (GOD_BILLING below); skip the real
    // billing-status edge fn — from localhost it just errors (CORS/401) and adds
    // console noise. Real auth/billing still runs under VITE_E2E and in prod.
    enabled: !!user && !GOD_MODE,
    refetchInterval: () => {
      const isRealUser = !!user && !user.is_anonymous;
      const isVisible =
        typeof document === 'undefined' ||
        document.visibilityState === 'visible';
      return isRealUser && isVisible ? 60000 : false;
    },
    queryFn: async (): Promise<BillingStatus> => {
      const { data, error } = await supabase.functions.invoke('billing-status');
      if (error) throw error;
      return data as BillingStatus;
    },
  });

  // Fetch user's profile data directly (avoiding circular dependency)
  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id || '')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Initialize notifications preference once on first render after profile loads
  useEffect(() => {
    if (profile?.notifications_enabled) void ensurePermission();
  }, [profile?.notifications_enabled]);

  // Keep the multi-account display snapshot (name + avatar path) in sync with
  // the loaded profile, so the switcher can render the active account's name and
  // avatar without a per-account fetch. Real users only (anon isn't stored).
  useEffect(() => {
    if (!user || user.is_anonymous) return;
    updateStoredProfile(user.id, {
      name: profile?.full_name ?? null,
      avatar: profile?.avatar_path ?? null,
    });
    window.dispatchEvent(new Event('tk-accounts-changed'));
  }, [user, profile?.full_name, profile?.avatar_path]);

  // Account claiming / guest-to-user conversion. The moment a real (non-
  // anonymous) account signs in for the first time, clone the Default user's
  // starter content into it. Keyed on the *raw* user (the real underlying
  // session), so anonymous visitors — and the staging god-mode shim, whose
  // underlying session stays anonymous — never auto-claim.
  useEffect(() => {
    if (!user || user.is_anonymous) return;
    if (isDefaultUser(user.email)) return; // the source account, never claims
    if (isAccountClaimed()) return;
    claimDefaultWorkspace()
      .then(() => {
        queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      })
      .catch((err) => console.error('claim default workspace failed', err));
  }, [user, queryClient]);

  // Set up real-time subscription for meshes table to update meshData immediately and notify the user
  useEffect(() => {
    if (!user) {
      return;
    }

    // Supabase realtime
    const channel = supabase
      .channel(`mesh-updates-${user.id}`)
      .on(
        'broadcast',
        {
          event: 'mesh-updated',
        },
        async ({ payload }) => {
          if (payload.kind === 'mesh') {
            // Prime the mesh row straight from the broadcast (status + file_type)
            // so the GLB download can start without a re-select round-trip. Only
            // patches an already-cached row (the pending row useMeshData polled
            // during generation); falls back to invalidate when there's none.
            const primed = queryClient.setQueryData(
              ['meshData', payload.id],
              (old: MeshData | undefined) =>
                old
                  ? {
                      ...old,
                      status: payload.status,
                      file_type: payload.file_type ?? old.file_type,
                    }
                  : old,
            );
            if (!primed) {
              queryClient.invalidateQueries({
                queryKey: ['meshData', payload.id],
              });
            }
            queryClient.invalidateQueries({ queryKey: ['mesh', payload.id] });
            queryClient.invalidateQueries({ queryKey: ['billing', 'status'] });

            if (
              payload.status === 'success' &&
              profile?.notifications_enabled &&
              !window.location.pathname.includes(
                `/editor/${payload.conversation_id}`,
              )
            ) {
              if (await ensurePermission()) {
                const notification = new Notification('3D model is ready', {
                  body: 'Your generated 3D model has finished. Click to open.',
                });
                notification.onclick = () => {
                  window.focus();
                  navigate(`/editor/${payload.conversation_id}`);
                  notification.close();
                };
              }
            }
          }

          if (payload.kind === 'preview') {
            queryClient.invalidateQueries({
              queryKey: ['preview', payload.id],
            });
          }

          // #10: fal's CDN URL, handed to the client BEFORE our own webhook
          // finishes fetching + reuploading it to Storage — lets the browser
          // start downloading the model several seconds earlier. fal's model
          // URLs are publicly CORS-enabled (Access-Control-Allow-Origin: *)
          // and cached immutable for 60 days (verified live), so this is safe
          // to fetch directly. ANY failure (network hiccup, a future change to
          // fal's CORS policy, etc.) is swallowed — the normal storage-backed
          // flow (the existing 'mesh' handler above + useMeshData) completes
          // the load exactly as it does today, so this can only accelerate a
          // load, never break one.
          if (payload.kind === 'mesh-fal-ready' && payload.fal_url) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            fetch(payload.fal_url, { signal: controller.signal })
              .then((res) => (res.ok ? res.blob() : null))
              .then((blob) => {
                if (blob) {
                  // Only fill an empty cache slot — never clobber a blob the
                  // normal storage-backed flow may have already delivered (it
                  // can complete first if this fetch is slow), avoiding a
                  // pointless re-parse/re-render of an already-displayed mesh.
                  queryClient.setQueryData(
                    ['mesh', payload.id],
                    (old: Blob | undefined) => old ?? blob,
                  );
                  void putCachedMeshBlob(payload.id, blob);
                }
              })
              .catch(() => {
                /* silent — the normal storage-backed flow still completes */
              })
              .finally(() => clearTimeout(timeoutId));
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, navigate, profile?.notifications_enabled]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    });
    if (signUpError) throw signUpError;
  };

  // Convert the live anonymous session into a permanent account WITHOUT minting a
  // new user — `updateUser` adds the email/password + name to the same user_id,
  // so every conversation, mesh, and storage object the guest already created
  // stays theirs. If "Confirm email" is on (it is), Supabase emails a
  // confirmation link; the user is permanent once they confirm. Surfaces the
  // usual error if the email already belongs to another account (the caller then
  // routes them to Log in).
  const convertAnonymousAccount = async (
    email: string,
    password: string,
    name: string,
  ) => {
    const { error } = await supabase.auth.updateUser({
      email,
      password,
      data: { full_name: name },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    if (error) throw error;
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  // God mode keeps the REAL user (real id from the real anon/sign-in session);
  // it only masks is_anonymous so an anon dev session isn't feature-gated. No
  // placeholder id, no fake session — dev now matches prod identity semantics.
  const godUser = GOD_MODE && user ? { ...user, is_anonymous: false } : user;

  // Anon-out-of-tokens harness (dev-only, flag-driven — see lib/devAnonZero).
  // When active it WINS over god mode: present the real session as anonymous and
  // report a zero (or N) token balance, so the whole app can be driven as a guest
  // who has run out. Null (and fully tree-shaken) in every built bundle.
  const anonZero = readAnonZeroOverride();
  const effectiveUser =
    anonZero && user ? { ...user, is_anonymous: true } : godUser;
  const anonZeroBilling: BillingStatus | null = anonZero
    ? {
        user: { hasTrialed: false },
        subscription: null,
        tokens: {
          free: anonZero.tokens,
          subscription: 0,
          purchased: 0,
          total: anonZero.tokens,
        },
      }
    : null;

  return (
    <AuthContext.Provider
      value={{
        session,
        user: effectiveUser,
        billing:
          anonZeroBilling ?? (GOD_MODE ? GOD_BILLING : (billing ?? null)),
        // Both modes reflect the REAL auth-resolution state so consumers never
        // query with a not-yet-resolved user. God mode (and the anon-zero
        // harness) just don't wait on the billing/profile fetches (billing is
        // faked). (issue #58 kept the shell off the billing call.)
        isLoading:
          GOD_MODE || anonZero
            ? isLoading
            : isLoading || (!!user && (isBillingLoading || isProfileLoading)),
        isAuthLoading: isLoading,
        signIn,
        signUp,
        convertAnonymousAccount,
        signInWithMagicLink,
        verifyOtp,
        signOut,
        resetPassword,
        updatePassword,
        accounts,
      }}
    >
      {children}
      {/* "Add another account" sign-in (#297). Rendered here so it's available
          from every account control (Sidebar + AppMenuBar). addAccount mode runs
          a fresh sign-in/sign-up — it never converts the current anon session
          (the current account is already stashed in the store). */}
      <AuthPopup
        open={accounts.addAccountOpen}
        onOpenChange={accounts.setAddAccountOpen}
        addAccount
        initialMode="login"
        title="Add another account"
        description="Sign in to a different account — you can switch between them anytime."
      />
    </AuthContext.Provider>
  );
}
